import { useContext, useEffect, type FC } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { federatedSignIn } from "../AuthApi";
import type { CognitoHostedUIIdentityProvider } from "@aws-amplify/auth/lib-esm/types/Auth";
import { AuthContext, DEFAULT_AUTHENTICATED_TARGET } from "../AuthContext";
import type { AuthContextType } from "../../types/AuthProvider";
import {
  consumePendingSamlProvider,
  consumePostLoginRedirect,
  isSafeRedirectPath,
  isValidProviderName,
  readRedirectParam,
  setPostLoginRedirect,
} from "../ssoSession";

const AuthSAML: FC = () => {
  const { provider } = useParams<{ provider: string }>();
  const history = useHistory();
  const location = useLocation();

  const {
    authState: { authenticated },
  } = useContext(AuthContext) as AuthContextType;

  useEffect(() => {
    // Consume the entry captured in index.tsx in BOTH branches below. If it
    // survived one of them it would leak into a later manual logout in the
    // same tab and silently re-login the user.
    const pending = consumePendingSamlProvider();

    // URLSearchParams, not split("?redirect="): the old parse only found the
    // param when it came first, so ?msp=acme&redirect=/x lost the deep link.
    // Validated as a same-origin relative path before storing.
    const redirectParam = readRedirectParam(location.search);
    const redirectPath = isSafeRedirectPath(redirectParam)
      ? redirectParam
      : (pending?.redirect ?? null);

    // Only write when this URL actually carries a target — preserving today's
    // behaviour of leaving a redirect stored earlier by ProtectedRoute alone.
    if (redirectPath) {
      setPostLoginRedirect(redirectPath);
    }

    // react-router v5 hands back the RAW segment, so decode before validating —
    // otherwise "/saml/a%20b" would sail through as the literal "a%20b".
    let providerName = provider ?? "";
    try {
      providerName = decodeURIComponent(providerName);
    } catch {
      // Malformed percent-encoding: keep the raw segment and let the check
      // below reject it if Cognito could not accept it either.
    }

    // The slug is live user input. A missing slug (/saml) or anything Cognito
    // could not accept as an identity provider name goes back to the login page
    // rather than into federatedSignIn, which would leave a blank route.
    if (!isValidProviderName(providerName)) {
      history.replace("/login");
      return;
    }

    if (authenticated) {
      history.push(redirectPath ?? DEFAULT_AUTHENTICATED_TARGET);
      consumePostLoginRedirect();
      return;
    }

    // providerName is a free-form SAML provider name from the URL param; cast
    // to the amplify enum type the API expects without changing the value.
    federatedSignIn({
      provider: providerName as CognitoHostedUIIdentityProvider,
    });
  }, [provider, authenticated, history, location.search]);

  return null;
};

export default AuthSAML;
